import type {
  TEntityId,
  IEntityDataItem,
  ISelectItem,
  IEntitySelectItem,
  IAddress,
  IEntityParentObjectForCreate,
} from '../models'
import type { IContactBase, TContact } from '../contact/models'
import type { ILocation } from '../location/models'
import type { ITeamMember } from '../teamMember/models'
import type { IContract } from '../contract/models'
import type { ICase } from '../case/models'
import type { IOverviewLastSelectedState } from '../overview/models'
import type { ICall } from '../call/models'
import type { IActivityEmail } from '../email/models'
import type { IMeeting } from '../meeting/models'
import type { ITask } from '../task/models'
import type { IProject } from '../project/models'
import type { IOpportunity, TAggregateTerm } from '../opportunity/models'
import type { IAccount } from '../account/models'
import type { IMSService, ITermBreakdown } from '../msService/models'
import { IOrderServiceOrder } from '../order/models'
import type {
  ICompanyDocument,
  ICompanyMSDocument,
  ICompanyMSServiceDocument,
} from '../document/models'
import type {
  ICompanyNote,
  ICompanyMSNote,
  ICompanyMSServiceNote,
} from '../note/models'
import type { IIndustry, ISubindustry } from './../industry/models'

export interface ICompanyOverview {
  company: ICompany
  companyMasterServices: ICompanyMasterService[]
  companyContacts: ICompanyContact[]
  companyTeamMembers: ICompanyTeamMember[]
  companyDocuments: ICompanyDocument[]
  companyNotes: ICompanyNote[]
  locations: ILocation[]
  opportunities: IOpportunity[]
  projects: IProject[]
  orderServiceOrders: IOrderServiceOrder[]
  tasks: ITask[]
  calls: ICall[]
  emails: IActivityEmail[]
  meetings: IMeeting[]
  serviceRequests: object[]
  cases: ICase[]
  contracts: IContract[]
  lastSelectedState: ICompanyLastSelectedState | undefined
  [key: string]: any
}

export interface ICompany extends IEntityDataItem {
  name: string
  parentCompany?: IEntitySelectItem
  relationship?: ISelectItem
  startsSeparateAccount?: boolean
  account: IAccount
  primaryContact?: ICompanyContact
  billingContact?: ICompanyContact
  industry?: IIndustry
  subindustry?: ISubindustry
  annualRevenue: number
  numEmployees: number
  employeeCount?: ISelectItem
  hasLoaOnFile: boolean
  loaExpirationDate: string | null
  address?: IAddress
  websiteUrl?: string
  status: ISelectItem
  [key: string]: any
}

export interface ICompanyParentObjectForCreate {
  entityName: 'company'
  object: IEntitySelectItem
  replyAttachableDocuments?: IEntityParentObjectForCreate['replyAttachableDocuments']
}

export interface ICompanyMasterServiceParentObjectForCreate {
  entityName: 'companyMasterService' | 'masterService'
  object: IEntitySelectItem
  [key: string]: any
}

export interface ICompanyMSServiceParentObjectForCreate {
  entityName: 'companyMSService'
  object: IEntitySelectItem
  [key: string]: any
}

export interface ICompanyLastSelectedState extends IOverviewLastSelectedState {
  companyId: TEntityId
  selectedMSId?: TEntityId | null
  selectedContractId?: TEntityId | null
}

export interface ICompanyMasterService extends IEntityDataItem {
  company: IEntitySelectItem
  orderMasterService?: IEntitySelectItem
  descr: string
  term?: TAggregateTerm
  termBreakdown?: ITermBreakdown // set when the msServices' terms vary
  numLocations?: number
  numServices?: number
  project?: IEntitySelectItem
  status: ISelectItem
  companyMSServices: ICompanyMSService[]
  companyMSContacts: ICompanyMSContact[]
  companyMSTeamMembers: ICompanyMSTeamMember[]
  companyMSDocuments?: ICompanyMSDocument[]
  companyMSNotes?: ICompanyMSNote[]
}

// Still Omit, so its fields are `any`: TOmitNamed exposes type errors in
// Order.ts's order-to-company copy.
export interface ICompanyMasterServiceForPost
  extends Omit<ICompanyMasterService, 'hrid' | 'companyMSServices'> {
  companyMSServices: ICompanyMSServiceForPost[]
}

export interface ICompanyMSService extends IMSService {
  type?: ISelectItem
  orderMSService?: IEntitySelectItem
  lastUpdatedDate?: string
  // 'lastUpdatedDate' above is a modified format modifiedAt date,
  // for screen display. Should probably be handled by a formatter. JR.
  lastSetActiveDate?: string
  lastSuspendedDate?: string
  lastDisconnectedDate?: string
  companyMSServiceDocuments?: ICompanyMSServiceDocument[]
  companyMSServiceNotes?: ICompanyMSServiceNote[]
}

// Still Omit, so its fields are `any`: TOmitNamed exposes type errors in
// Order.ts's order-to-company copy.
export interface ICompanyMSServiceForPost
  extends Omit<ICompanyMSService, 'hrid'> {}

// Company TeamMember

interface ICompanyTeamMemberBase extends IEntityDataItem {
  teamMember: ITeamMember
  role: ISelectItem
  status: ISelectItem
}

export interface ICompanyTeamMember extends ICompanyTeamMemberBase {
  company: IEntitySelectItem
}

export interface ICompanyMSTeamMember extends ICompanyTeamMemberBase {
  companyMasterService: IEntitySelectItem
}

// Company MS Contact

export interface ICompanyContact extends IContactBase {
  company: ISelectItem
  isPrimaryContact?: boolean
  isBillingContact?: boolean
  tsd?: never
  supplier?: never
  leadSource?: never
  [key: string]: any
}

export interface ICompanyMSContact extends IEntityDataItem {
  companyMasterService: IEntitySelectItem
  contact: TContact
  label?: string
  splitFraction: number | null
  status: ISelectItem
  [key: string]: any
}

export function isCompany(val: unknown): val is ICompany {
  return (val as ICompany).hasOwnProperty('account')
  // Not great, using 'account', but there are no other
  // properties any more unique to the company entity.
}

export function isCompanyMasterServices(
  val: unknown
): val is ICompanyMasterService[] {
  return (
    Array.isArray(val) &&
    val.length > 0 &&
    (val as ICompanyMasterService[])[0].hasOwnProperty('companyMSServices')
  )
}
